import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { Upload, Image as ImageIcon, X, Loader2 } from "lucide-react";

/**
 * Drag-and-drop image uploader.
 * Calls POST /api/admin/uploads/image and returns the full absolute URL via onChange.
 *
 * Props:
 *  - value: current image URL (absolute or "/api/uploads/...")
 *  - onChange(url): called with the new URL after upload, or "" after clear
 *  - aspect: tailwind aspect-* class for the preview (default aspect-[4/3])
 *  - testId: prefix for data-testid
 */
export default function ImageUploader({ value, onChange, aspect = "aspect-[4/3]", testId = "image-uploader" }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [dragOver, setDragOver] = useState(false);

    const backend = process.env.REACT_APP_BACKEND_URL;
    const previewSrc = !value
        ? null
        : value.startsWith("http")
            ? value
            : `${backend}${value}`;

    const doUpload = async (file) => {
        setError("");
        if (!file) return;
        if (!file.type?.startsWith("image/")) {
            setError("Selecione um arquivo de imagem.");
            return;
        }
        if (file.size > 25 * 1024 * 1024) {
            setError("Arquivo muito grande. Máximo: 25 MB (será comprimido automaticamente).");
            return;
        }
        setUploading(true);
        try {
            // Compress in-browser before upload (resizes to max 2000px edge, JPEG 0.85)
            const compressed = await compressImage(file);
            const form = new FormData();
            form.append("file", compressed);
            const { data } = await api.post("/admin/uploads/image", form, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            onChange(data.url);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        doUpload(file);
    };

    const clear = () => onChange("");

    return (
        <div data-testid={testId} className="space-y-3">
            {previewSrc ? (
                <div className={`relative ${aspect} w-full rounded-2xl overflow-hidden bg-[#FAFAF7] border border-black/10`}>
                    <img src={previewSrc} alt="preview" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3 flex justify-between gap-2">
                        <button type="button" onClick={() => inputRef.current?.click()} data-testid={`${testId}-replace`}
                                disabled={uploading}
                                className="btn-ghost !bg-white/90 !py-2 !px-3 text-xs">
                            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                            Trocar
                        </button>
                        <button type="button" onClick={clear} data-testid={`${testId}-clear`}
                                className="btn-ghost !bg-white/90 !py-2 !px-3 text-xs !text-red-600 !border-red-200">
                            <X className="w-3.5 h-3.5" /> Remover
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    data-testid={`${testId}-dropzone`}
                    className={`w-full ${aspect} max-h-[280px] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-3 transition-all
                        ${dragOver ? "border-[#00D4D4] bg-[#00D4D4]/5" : "border-black/15 bg-[#FAFAF7] hover:border-black/30 hover:bg-white"}`}
                >
                    {uploading ? (
                        <>
                            <Loader2 className="w-7 h-7 text-[#5C5C5C] animate-spin" />
                            <div className="font-display text-sm text-[#5C5C5C]">Enviando imagem...</div>
                        </>
                    ) : (
                        <>
                            <div className="w-12 h-12 rounded-full bg-white border border-black/5 flex items-center justify-center">
                                <ImageIcon className="w-5 h-5 text-[#5C5C5C]" />
                            </div>
                            <div className="text-center">
                                <div className="font-display text-sm">Clique ou arraste a foto aqui</div>
                                <div className="font-body text-xs text-[#5C5C5C] mt-1">PNG, JPG, WebP — compressão automática</div>
                            </div>
                        </>
                    )}
                </button>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                className="hidden"
                data-testid={`${testId}-input`}
                onChange={(e) => doUpload(e.target.files?.[0])}
            />

            {error && (
                <div data-testid={`${testId}-error`} className="text-xs text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">
                    {error}
                </div>
            )}
        </div>
    );
}
